/**
 * Announcements Page
 * 
 * Company-wide announcements and updates.
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { 
  Megaphone, 
  Plus,
  Pin,
  Users,
  Calendar,
  Loader2,
  ChevronRight,
  Eye,
} from 'lucide-react'
import { cn, formatRelativeTime, getInitials, getAvatarColor } from '../lib/utils'
import { announcementsApi, type Announcement } from '../lib/api'

interface AnnouncementsPageProps {
  showCreate?: boolean
}

export default function AnnouncementsPage({ showCreate = false }: AnnouncementsPageProps) {
  const navigate = useNavigate()
  
  // Fetch announcements
  const { data: announcements = [], isLoading } = useQuery({
    queryKey: ['teams-announcements'],
    queryFn: announcementsApi.list,
  })
  
  // Separate pinned
  const pinnedAnnouncements = announcements.filter(a => a.is_pinned)
  const regularAnnouncements = announcements.filter(a => !a.is_pinned)

  return (
    <div className="h-full bg-slate-50 dark:bg-slate-900">
      <div className="max-w-4xl mx-auto px-6 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-1">Announcements</h1>
            <p className="text-slate-500 dark:text-slate-400">
              Company-wide updates and news
            </p>
          </div>
          <button
            onClick={() => navigate('/announcements/new')}
            className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg font-medium transition-colors"
          >
            <Plus className="w-4 h-4" />
            New Announcement
          </button>
        </div>
        
        {/* Announcements List */}
        {isLoading ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-violet-500 mb-3" />
            <p className="text-sm text-slate-400">Loading announcements...</p>
          </div>
        ) : announcements.length === 0 ? (
          <div className="text-center py-12 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
            <Megaphone className="w-12 h-12 mx-auto text-slate-300 dark:text-slate-600 mb-4" />
            <p className="text-lg font-medium text-slate-700 dark:text-slate-300 mb-2">No announcements yet</p>
            <p className="text-sm text-slate-400 mb-4">
              Company-wide updates will appear here
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Pinned */}
            {pinnedAnnouncements.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 px-1 flex items-center gap-1">
                  <Pin className="w-3 h-3" />
                  Pinned
                </h3>
                <div className="space-y-3">
                  {pinnedAnnouncements.map((announcement) => (
                    <AnnouncementCard key={announcement.id} announcement={announcement} />
                  ))}
                </div>
              </div>
            )}
            
            {/* Recent */}
            <div>
              {pinnedAnnouncements.length > 0 && (
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 px-1">
                  Recent
                </h3>
              )}
              <div className="space-y-3">
                {regularAnnouncements.map((announcement) => (
                  <AnnouncementCard key={announcement.id} announcement={announcement} />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// Announcement Card
function AnnouncementCard({ announcement }: { announcement: Announcement }) {
  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden hover:shadow-md transition-shadow">
      <div className="p-5">
        {/* Header */}
        <div className="flex items-start gap-4 mb-4">
          <div className={cn(
            "w-12 h-12 rounded-xl flex items-center justify-center text-white text-lg font-semibold bg-gradient-to-br flex-shrink-0",
            getAvatarColor(announcement.author?.name || '')
          )}>
            {getInitials(announcement.author?.name || '', '')}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-bold text-lg text-slate-800 dark:text-white">{announcement.title}</h3>
              {announcement.is_pinned && (
                <Pin className="w-4 h-4 text-amber-500" />
              )}
            </div>
            <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
              <span className="flex items-center gap-1">
                <Users className="w-3.5 h-3.5" />
                {announcement.author?.name}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" />
                {formatRelativeTime(announcement.created_at)}
              </span>
            </div>
          </div>
        </div>
        
        {/* Content */}
        <p className="text-slate-700 dark:text-slate-300 whitespace-pre-wrap">
          {announcement.content}
        </p>
      </div>
      
      {/* Footer */}
      <div className="flex items-center justify-between px-5 py-3 bg-slate-50 dark:bg-slate-900 border-t border-slate-100 dark:border-slate-700">
        <span className="text-xs text-slate-500 flex items-center gap-1">
          <Eye className="w-3.5 h-3.5" />
          {announcement.read_count} views
        </span>
        <button className="text-xs text-violet-600 hover:text-violet-700 font-medium flex items-center gap-1">
          Read more
          <ChevronRight className="w-3 h-3" />
        </button>
      </div>
    </div>
  )
}
